import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Clock, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_STYLES = {
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  queued: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function ScrapeHistory() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // job_id -> bool
  // Firecrawl + Apify usage now comes from /system/credit-tracker so the
  // banner here is guaranteed to match the Dashboard's CreditTrackerCard.
  // (Previously this pulled /system/scraping-settings which only tracked
  // the local counter, so numbers would drift.)
  const [tracker, setTracker] = useState(null);

  const load = async () => {
    try {
      const [jobsRes, trackerRes] = await Promise.all([
        api.get("/scrape-jobs", { params: { limit: 200 } }),
        api.get("/system/credit-tracker", { params: { limit: 1 } }).catch(() => ({ data: null })),
      ]);
      setJobs(jobsRes.data);
      if (trackerRes.data) setTracker(trackerRes.data);
    } catch (e) {
      toast.error("Failed to load jobs", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const totalCreditsThisView = jobs.reduce(
    (acc, j) => acc + (Number(j?.summary?.firecrawl_credits) || 0),
    0
  );

  return (
    <div className="space-y-5" data-testid="scrape-history-page">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Diagnostics</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
            Scrape History
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Every find / scrape / enrich job is recorded here with status, timings,
            summary counts, and errors.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="min-h-[44px]">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <CreditsBanner
        tracker={tracker}
        viewCredits={totalCreditsThisView}
      />

      {loading ? (
        <div className="text-sm text-slate-500 p-6">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          <History className="h-7 w-7 mx-auto text-slate-300 mb-3" />
          No jobs have run yet. Trigger one from Brokerages or Realtors.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm data-table">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Target</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Started</th>
                <th className="px-3 py-2 text-left">Duration</th>
                <th className="px-3 py-2 text-left">Engine</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Cost</th>
                <th className="px-3 py-2 text-left">Summary</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const isOpen = !!expanded[j.id];
                const credits = Number(j?.summary?.firecrawl_credits) || 0;
                const engine = j.engine || j?.summary?.engine || "";
                return (
                  <React.Fragment key={j.id}>
                    <tr className="border-t border-slate-200" data-testid={`job-row-${j.id}`}>
                      <td className="px-3 py-2 text-slate-700 align-middle">{j.type}</td>
                      <td className="px-3 py-2 text-slate-600 align-middle truncate max-w-[18rem]">
                        {j.target_label || "—"}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span
                          className={`px-2 py-0.5 text-xs rounded border inline-flex items-center gap-1 ${
                            STATUS_STYLES[j.status] || STATUS_STYLES.queued
                          }`}
                        >
                          {j.status === "running" || j.status === "queued" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : j.status === "failed" ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : j.status === "done" ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <Clock className="h-3 w-3" />
                          )}
                          {j.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600 align-middle whitespace-nowrap">
                        {fmtTime(j.started_at)}
                      </td>
                      <td className="px-3 py-2 text-slate-600 align-middle whitespace-nowrap tabular-nums">
                        {duration(j.started_at, j.finished_at)}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <EngineBadge engine={engine} />
                      </td>
                      <td
                        className="px-3 py-2 text-right align-middle whitespace-nowrap tabular-nums"
                        data-testid={`job-cost-${j.id}`}
                      >
                        {(() => {
                          const e = (engine || "").toLowerCase();
                          const apifyUsd = Number(j?.summary?.apify_cost_usd) || 0;
                          if (e === "apify" || apifyUsd > 0) {
                            return (
                              <span className="inline-flex items-center gap-1 text-slate-700">
                                <Coins className="h-3.5 w-3.5 text-amber-500" />
                                ${apifyUsd.toFixed(apifyUsd < 0.1 ? 4 : 2)}
                              </span>
                            );
                          }
                          if (credits > 0) {
                            return (
                              <span className="inline-flex items-center gap-1 text-slate-700">
                                <Coins className="h-3.5 w-3.5 text-amber-500" />
                                {credits}
                              </span>
                            );
                          }
                          return <span className="text-slate-300">0</span>;
                        })()}
                      </td>
                      <td className="px-3 py-2 text-slate-600 align-middle">
                        <SummaryInline summary={j.summary} />
                      </td>
                      <td className="px-3 py-2 text-right align-middle">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setExpanded((s) => ({ ...s, [j.id]: !s[j.id] }))
                          }
                          data-testid={`expand-job-${j.id}`}
                        >
                          {isOpen ? "Hide" : "Details"}
                        </Button>
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="bg-slate-50 border-t border-slate-200">
                        <td colSpan={9} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                            <div>
                              <div className="font-medium text-slate-500 mb-1">Job</div>
                              <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto">
{JSON.stringify({ id: j.id, by: j.created_by, engine: j.engine }, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="font-medium text-slate-500 mb-1">Summary</div>
                              <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto">
{JSON.stringify(j.summary || {}, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="font-medium text-slate-500 mb-1">Errors</div>
                              <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto">
{(j.errors || []).join("\n") || "(none)"}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreditsBanner({ tracker, viewCredits }) {
  if (!tracker) return null;
  const fc = tracker.firecrawl || {};
  const used = Number(fc.credits_used) || 0;
  const cap = Number(fc.credit_cap) || 0;
  const remaining = Number(fc.credits_remaining ?? Math.max(0, cap - used));
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const month = fc.month || "";
  const paused = fc.disabled_reason === "credit_threshold";
  const configured = !!fc.configured;
  // "vendor_api" means we've synced live from Firecrawl; "local_counter"
  // means we're falling back to our own running total. Surface it so the
  // user knows why the number might occasionally lag reality.
  const source = fc.source || "local_counter";
  const syncedAt = fc.last_synced_at || "";
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid="scrape-history-credits-banner"
    >
      <div className="flex items-center gap-3">
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2">
          <Coins className="h-4 w-4 text-amber-600" />
        </div>
        <div className="text-sm">
          <div className="font-medium text-slate-900">
            Firecrawl credits remaining this month:{" "}
            <span data-testid="firecrawl-credits-remaining" className="tabular-nums">
              {remaining.toLocaleString()}
            </span>{" "}
            / <span className="tabular-nums">{cap.toLocaleString()}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {used.toLocaleString()} used in {month || "this month"}
            {viewCredits > 0 ? (
              <>
                {" · "}
                <span className="text-slate-600">
                  {viewCredits.toLocaleString()} across visible jobs
                </span>
              </>
            ) : null}
            {!configured ? (
              <span className="text-slate-500"> · Firecrawl not configured</span>
            ) : paused ? (
              <span className="text-amber-700"> · auto-paused at 80% — Playwright handles new jobs until next month</span>
            ) : null}
            {source === "vendor_api" && syncedAt ? (
              <span
                className="text-slate-400 ml-1"
                title={`Live-synced from Firecrawl · ${new Date(syncedAt).toLocaleString()}`}
              > · live</span>
            ) : source === "local_counter" ? (
              <span className="text-slate-400 ml-1" title="Local counter — click Refresh on Dashboard to sync from Firecrawl">
                {" "}· estimate
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="hidden sm:block w-full max-w-xs">
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full ${paused ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-[10px] text-slate-500 text-right mt-1 tabular-nums">{pct}% used</div>
      </div>
    </div>
  );
}

function EngineBadge({ engine }) {
  if (!engine) return <span className="text-slate-300 text-xs">—</span>;
  const e = String(engine).toLowerCase();
  const cls =
    e === "firecrawl"
      ? "bg-violet-50 text-violet-700 border-violet-200"
      : e === "playwright"
      ? "bg-sky-50 text-sky-700 border-sky-200"
      : e === "apify" || e === "playwright_realtorca"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : e === "error"
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`px-2 py-0.5 text-xs rounded border inline-flex ${cls}`}>{engine}</span>
  );
}

function SummaryInline({ summary }) {
  if (!summary || Object.keys(summary).length === 0) return <span className="text-slate-400">—</span>;
  // Skip engine/credits/pages since they have dedicated columns or are shown elsewhere
  const skip = new Set(["engine", "firecrawl_credits", "pages_crawled"]);
  const entries = Object.entries(summary).filter(([k]) => !skip.has(k));
  if (entries.length === 0) return <span className="text-slate-400">—</span>;
  const parts = entries.slice(0, 3).map(([k, v]) => {
    if (Array.isArray(v)) return `${k}: ${v.length}`;
    if (typeof v === "object") return `${k}: …`;
    return `${k}: ${v}`;
  });
  return <span className="text-slate-600 truncate inline-block max-w-[20rem]">{parts.join(" · ")}</span>;
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
}
function duration(start, end) {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const sec = Math.max(0, Math.round((e - s) / 1000));
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}
